/**
 * search-index.js
 *
 * Pre-computes normalized tokens per item so scoring never calls
 * normalizeText() on raw data on every keystroke.
 *
 * Index shape per item:
 *   _raw       — original item object (returned as result)
 *   _normFields — { fieldName: normalizedString, ... }
 *   _tokens    — unique normalized word tokens from all indexed fields
 *   _initials  — first char of every token joined (e.g. "jp" for "Juan Pérez")
 */

import { normalizeText } from "@/lib/search-rank";
import { expandQuery } from "@/lib/search-synonyms";

const STATUS_FIELDS = new Set(["status", "category"]);

/**
 * Build a single index entry for one raw item.
 *
 * @param {Object} item
 * @param {string[]} fields
 * @returns {{ _raw: Object, _normFields: Object, _tokens: string[], _initials: string }}
 */
export function indexItem(item, fields) {
  const _normFields = {};
  const tokenSet = new Set();

  for (const field of fields) {
    const raw = item[field];
    if (!raw) continue;

    // Arrays (e.g. tags: string[]) are joined so each element becomes an independent token
    const str = Array.isArray(raw) ? raw.join(" ") : raw;
    const norm = normalizeText(str);
    _normFields[field] = norm;
    norm.split(/\s+/).filter(Boolean).forEach((t) => tokenSet.add(t));

    // Pre-expand synonyms for status/category so synonym hits score on
    // the index pass without running expandQuery() per keystroke.
    if (STATUS_FIELDS.has(field)) {
      const expanded = expandQuery(raw);
      expanded.slice(1).forEach((syn) => {
        normalizeText(syn)
          .split(/\s+/)
          .filter(Boolean)
          .forEach((t) => tokenSet.add(t));
      });
    }
  }

  const _tokens = Array.from(tokenSet);
  const _initials = _tokens.map((t) => t[0] ?? "").join("");

  return { _raw: item, _normFields, _tokens, _initials };
}

/**
 * Build a full index for an array of items.
 *
 * @param {Object[]} items
 * @param {string[]} fields
 * @returns {Array} indexed items
 */
export function buildIndex(items, fields) {
  if (!items?.length || !fields?.length) return [];
  return items.map((item) => indexItem(item, fields));
}

/**
 * Score a pre-indexed item against an already-normalized query.
 *
 * Scoring levels (same scale as scoreMatch in search-rank.js):
 *   3 = exact field match
 *   2 = field starts with query
 *   1 = field contains query
 *   0.5 = fuzzy: all query words match token prefixes (≥2 words)
 *   0 = no match
 *
 * @param {{ _normFields: Object, _tokens: string[], _initials: string }} indexed
 * @param {string} normQuery — already normalized
 * @param {string[]} qWords  — query split into words (avoids re-splitting per item)
 * @returns {number}
 */
function scoreIndexed(indexed, normQuery, qWords) {
  const { _normFields, _tokens } = indexed;
  let best = 0;

  for (const norm of Object.values(_normFields)) {
    if (!norm) continue;
    if (norm === normQuery) return 3; // early exit on exact match
    if (norm.startsWith(normQuery)) { best = Math.max(best, 2); continue; }
    if (norm.includes(normQuery)) { best = Math.max(best, 1); }
  }

  if (best >= 2) return best;

  // Token-level scan: caps at 2 (token exact = a word match, not full-field exact).
  // This lets "perez" find "Juan Pérez" at score 2 instead of 1 (includes).
  for (const token of _tokens) {
    if (token === normQuery || token.startsWith(normQuery)) {
      best = Math.max(best, 2);
    } else if (token.includes(normQuery)) {
      best = Math.max(best, 1);
    }
  }

  if (best > 0) return best;

  // Fuzzy: every query word must be a prefix of some token (≥2 query words)
  if (qWords.length >= 2) {
    const allMatch = qWords.every((qw) => _tokens.some((t) => t.startsWith(qw)));
    if (allMatch) return 0.5;
  }

  return 0;
}

/**
 * Filter and rank pre-indexed items by query.
 * Drop-in replacement for sortByScore() that operates on indexed data.
 *
 * @param {Array} indexedItems — output of buildIndex()
 * @param {string} query
 * @param {number} [limit=25]
 * @returns {Object[]} raw items sorted by score, descending
 */
export function sortIndexed(indexedItems, query, limit = 25) {
  if (!query?.trim() || !indexedItems?.length) return [];
  const normQuery = normalizeText(query);
  const qWords = normQuery.split(/\s+/).filter(Boolean);

  return indexedItems
    .map((indexed) => ({ indexed, score: scoreIndexed(indexed, normQuery, qWords) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.indexed._raw);
}

/**
 * Union search over multiple query terms on pre-indexed data.
 * Mirrors unionSearch() in useSearchData but uses the indexed path.
 * First-seen wins (primary term ranked above synonyms).
 *
 * @param {Array} indexedItems — output of buildIndex()
 * @param {string[]} terms — [primaryQuery, ...synonyms]
 * @param {number} [limit=25]
 * @returns {Object[]} raw items, deduplicated, ordered by first occurrence
 */
export function unionSortIndexed(indexedItems, terms, limit = 25) {
  if (!terms?.length || !indexedItems?.length) return [];
  const seen = new Set();
  const results = [];
  for (const term of terms) {
    for (const item of sortIndexed(indexedItems, term, limit)) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        results.push(item);
        if (results.length >= limit) return results;
      }
    }
  }
  return results;
}
